import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { trigger, state, style, transition, animate } from '@angular/animations';
import { GoogleReviewsService, GoogleReviewsResponse , GoogleReview} from '../../services/google-reviews.service';
import { AutoScrollDirective } from '../../directives/auto-scroll.directive';
import { ScrollControlComponent } from '../shared/scroll-control/scroll-control.component';

@Component({
  selector: 'app-landing',
  standalone: true,
  imports: [
    CommonModule, 
    RouterModule, 
    FormsModule,
    AutoScrollDirective,
  ],
  templateUrl: './landing.component.html',
  styleUrl: './landing.component.scss',
  animations: [
    trigger('fadeIn', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(20px)' }),
        animate('600ms ease-out', style({ opacity: 1, transform: 'translateY(0)' }))
      ])
    ]),
    trigger('slideIn', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateX(-30px)' }),
        animate('500ms ease-out', style({ opacity: 1, transform: 'translateX(0)' }))
      ])
    ])
  ]
})
export class LandingComponent implements OnInit, OnDestroy {
  // Navigation state
  mobileMenuOpen = false;
  headerScrolled = false;
  mobileDropdowns: { [key: string]: boolean } = {
    'assurances': false,
    'brokins': false
  };

  // Google Reviews
  googleReviews: GoogleReview[] = [];
  reviewGroups: GoogleReview[][] = []; // Groups of 3 reviews
  averageRating: number = 0;
  totalRatings: number = 0;
  placeName: string = '';
  reviewsLoading: boolean = true;
  reviewsError: string = '';
  currentReviewIndex: number = 0;
  Math = Math;

  // Services data
  services = [
    {
      icon: 'pi pi-users',
      title: 'Assurance des Personnes pour Particuliers',
      description: 'Tous les contrats d\'assurance Mutuelle Santé, Prévoyance, Dommages Ouvrage, Crédit et Mandat pour assurer les incidenties, les accidents, la maladie, le décès des particuliers en France ou à l\'étranger.',
      link: '/assurance-credit-emprunteur'
    },
    {
      icon: 'pi pi-briefcase',
      title: 'Assurance des Personnes pour Professionnels et Entreprises',
      description: 'Tous les contrats d\'assurance Mutuelle Santé, Prévoyance Professionnelle Imputatreur, Cadre et Marié pour assurer les maladies, les accidents, la maladie, le décès des Professionnels et salariés d\'Entreprises en France ou à l\'étranger.',
      link: '/assurance-retraite-complementaire-pro'
    },
    {
      icon: 'pi pi-home',
      title: 'Assurance des Biens pour Particuliers',
      description: 'Tous les contrats d\'assurance Dommages aux Biens pour l\'habitation, l\'automobile, mais lès à la construction et d\'immobilier en général des particuliers en France ou à l\'étranger.',
      link: '/assurance-auto-particulier'
    },
    {
      icon: 'pi pi-building',
      title: 'Assurance des Biens pour Professionnels et Entreprises',
      description: 'Tous les contrats d\'assurance Dommages aux Biens pour les locaux, commerces, des filmes sees lie à la construction et d\'immobilier en général des Professionnels et Entreprises en France ou à l\'étranger.',
      link: '/assurance-multirisque-pro'
    },
    {
      icon: 'pi pi-shield',
      title: 'Assurance de l\'Activité des Professionnels et Entreprises',
      description: 'Tous les contrats d\'assurance Responsabilité Civile, Protection Juridique des Professionnels et Entreprises en France ou à l\'étranger.',
      link: '/assurance-rc-entreprise'
    },
    {
      icon: 'pi pi-ellipsis-h',
      title: 'Et bien d\'autres assurances',
      description: 'Grâce à les partenaires spécialisés, vous des assurances de Loyer, de toutes les propriétés, nous pourvez trouver des solutions d\'assurances à vos problèmes.',
      link: '/autres-assurances'
    }
  ];

  // Approach steps
  approachSteps = [
    {
      number: '1',
      title: 'Analyse de votre situation',
      description: 'Nous prenons le temps de comprendre vos besoins spécifiques et votre situation personnelle ou professionnelle.',
      icon: 'pi pi-search'
    },
    {
      number: '2',
      title: 'Sélection des meilleures offres',
      description: 'Grâce à notre réseau de partenaires, nous comparons et sélectionnons les contrats les plus adaptés à votre profil.',
      icon: 'pi pi-filter'
    },
    {
      number: '3',
      title: 'Accompagnement personnalisé',
      description: 'Nous vous guidons dans vos démarches et restons à vos côtés pour toute question ou modification de contrat.',
      icon: 'pi pi-user'
    },
    {
      number: '4',
      title: 'Suivi continu',
      description: 'Nous assurons un suivi régulier de vos contrats et vous informons des meilleures opportunités du marché.',
      icon: 'pi pi-chart-line'
    }
  ];


  // Team members
  teamMembers = [
    {
      name: 'Yann Rivière',
      role: 'Directeur général',
      image: 'assets/images/team/yann.jpg',
      bio: 'Expert en assurances avec plus de 15 ans d\'expérience'
    },
    {
      name: 'Sarah Martin',
      role: 'Responsable Particuliers',
      image: 'assets/images/team/sarah.jpg',
      bio: 'Spécialiste des assurances pour particuliers'
    },
    {
      name: 'Pierre Dubois',
      role: 'Responsable Entreprises',
      image: 'assets/images/team/pierre.jpg',
      bio: 'Expert en solutions d\'assurance professionnelles'
    }
  ];

  // Stats
  stats = [
    { value: '42', label: 'Assureurs et mutuelles partenaires' },
    { value: '3 752', label: 'Clients assurés' },
    { value: '48', label: 'Types d\'assurance proposés' }
  ];

  // Partners logos (carousel)
  partners = [
    'swisslife',
    'neoliane',
    'afiesca',
    'generali',
    'utwin',
    'april',
    'cardif',
    'metlife'
  ];

  currentPartnerIndex = 0;

  // Contact form
  contactForm = {
    prenom: '',
    nom: '',
    email: '',
    telephone: '',
    sujet: '',
    message: ''
  };

  formSubmitted = false;
  formSuccess = false;

  constructor(
    private router: Router,
    private googleReviewsService: GoogleReviewsService
  ) {}

  ngOnInit(): void {
    // Listen to scroll events
    window.addEventListener('scroll', this.onScroll.bind(this));
    
    // Start partner carousel
    this.startPartnerCarousel();
    // ✅ Force video to play
    this.playHeroVideo();
    // ✅ Load Google reviews
    this.loadGoogleReviews();
  }

    private playHeroVideo(): void {
    setTimeout(() => {
      const video = document.querySelector('.hero-video') as HTMLVideoElement;
      if (video) {
        video.muted = true;
        video.play().catch(error => {
          console.warn('Autoplay was prevented:', error);
          // Fallback: try to play on user interaction
          document.addEventListener('click', () => {
            video.play().catch(e => console.error('Video play failed:', e));
          }, { once: true });
        });
      }
    }, 100);
  }

  ngOnDestroy(): void {
    window.removeEventListener('scroll', this.onScroll.bind(this));
  }

  onScroll(): void {
    this.headerScrolled = window.scrollY > 50;
  }

  toggleMobileMenu(): void {
    this.mobileMenuOpen = !this.mobileMenuOpen;
    // Reset dropdowns when closing menu
    if (!this.mobileMenuOpen) {
      this.mobileDropdowns = {
        'assurances': false,
        'brokins': false
      };
    }
  }

  toggleMobileDropdown(key: string): void {
    this.mobileDropdowns[key] = !this.mobileDropdowns[key];
  }

  scrollToSection(sectionId: string): void {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      this.mobileMenuOpen = false;
    }
  }

  navigateToComparison(): void {
    this.router.navigate(['/devis']);
  }

  // Partner carousel
  startPartnerCarousel(): void {
    setInterval(() => {
      this.currentPartnerIndex = (this.currentPartnerIndex + 1) % this.partners.length;
    }, 3000);
  }

  // Contact form
  submitContactForm(): void {
    this.formSubmitted = true;
    
    // Validate form
    if (!this.contactForm.prenom || !this.contactForm.nom || 
        !this.contactForm.email || !this.contactForm.telephone) {
      return;
    }

    // TODO: Send form data to backend
    console.log('Form submitted:', this.contactForm);
    
    // Show success message
    this.formSuccess = true;
    
    // Reset form after 3 seconds
    setTimeout(() => {
      this.contactForm = {
        prenom: '',
        nom: '',
        email: '',
        telephone: '',
        sujet: '',
        message: ''
      };
      this.formSubmitted = false;
      this.formSuccess = false;
    }, 3000);
  }

  scrollToTop(): void {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  // ✅ Load Google Reviews
  loadGoogleReviews(): void {
    this.reviewsLoading = true;
    this.googleReviewsService.getReviews().subscribe({
      next: (response: GoogleReviewsResponse) => {
        this.googleReviews = response.reviews;
        this.averageRating = response.rating;
        this.totalRatings = response.total_ratings;
        this.placeName = response.place_name;
        
        // Group reviews by 3
        this.reviewGroups = this.groupReviews(this.googleReviews, 3);
        
        this.reviewsLoading = false;
      },
      error: (error) => {
        console.error('Error loading reviews:', error);
        this.reviewsError = 'Impossible de charger les avis pour le moment.';
        this.reviewsLoading = false;
      }
    });
  }

  // ✅ Group reviews into sets of N
  groupReviews(reviews: GoogleReview[], groupSize: number): GoogleReview[][] {
    const groups: GoogleReview[][] = [];
    for (let i = 0; i < reviews.length; i += groupSize) {
      groups.push(reviews.slice(i, i + groupSize));
    }
    return groups;
  }

  // ✅ Helper method to generate star array
  getStarArray(rating: number): boolean[] {
    return Array(5).fill(false).map((_, index) => index < rating);
  }

  // ✅ Carousel navigation methods for review groups
  nextReviewGroup(): void {
    if (this.currentReviewIndex < this.reviewGroups.length - 1) {
      this.currentReviewIndex++;
    }
  }

  previousReviewGroup(): void {
    if (this.currentReviewIndex > 0) {
      this.currentReviewIndex--;
    }
  }

  goToReviewGroup(index: number): void {
    this.currentReviewIndex = index;
  }
}